/// <reference types="@wix/ambassador-bootstrap-plugin/dist/src/domain" />

//Needs to auto inject gwClient to context

import { ApiGwClient } from '@wix/wix-bootstrap-api-gw-client';

import {
  Category as AmbassadorCategory,
  Service as AmbassadorService,
} from '@wix/ambassador-marketplace-categories-v1-category/types';
import {
  Project,
  Review,
} from '@wix/ambassador-partners-marketplace-v1-project/types';
import { PartnerProfile } from '@wix/ambassador-partners-profile-v1-partner-profile/types';
import { PartnerAccount } from '@wix/ambassador-partners-accounts-server/types';
import {
  Media,
  MediaSource,
  TemplateListing,
} from '@wix/ambassador-partners-marketplace-v1-template-listing/types';
import { Platform } from '@wix/ambassador-wixstudio-templates-v1-template/types';
import type { AspectStore } from '@wix/wix-aspects';
import type { BootstrapContext } from '@wix/wix-bootstrap-api';
import type { HadronClient } from '@wix/wix-bootstrap-hadron';
import type { ExperimentsBag } from '@wix/wix-experiments';
import { Messages } from '@wix/fe-essentials-standalone/i18n';
import type { NextApiRequest, NextApiResponse } from 'next';
import type { ParsedUrlQuery } from 'querystring';
import {
  CANCELATION_REASON_KEYS,
  PROFILE_PAGE_SECTIONS,
  PROFILE_NOT_EU_VALID,
  PROFILE_NOT_FOUND,
} from '../utils/constants';
import { Type } from '@wix/ambassador-partners-marketplace-v1-providerstate/types';
export type { QueryParams } from '@utils/route';
export { LANGUAGES_WITHOUT_ALL };

export type UserDetails = {
  email?: string;
  language?: string;
  userName?: string;
};

export type UserLocation = {
  country?: string;
  state?: string;
};

export type Language = { value: string; id: string };

export type ReviewListingRequestParams = {
  text?: string;
  rating?: number;
  listingId: string;
};

export type ReviewsInfo = {
  reviews: ReviewData[];
  total: number;
  average: number;
};

export interface ReviewData extends Review {
  serviceId?: string | null;
  clientName?: string;
  url?: string;
  id?: string | null;
  revision?: string | null;
}

export interface ClientProject
  extends Pick<
    Project,
    | 'id'
    | 'serviceId'
    | 'review'
    | 'status'
    | 'createdDate'
    | 'completedProjectSnapshot'
    | 'revision'
  > {
  clientName?: string;
  service?: string;
  description?: string;
  url?: string;
  briefUrl?: string;
  metaSiteId?: string;
  budget?: any;
}

export type CancelationReasonKey = keyof typeof CANCELATION_REASON_KEYS;

export interface IParams extends ParsedUrlQuery {
  all?: any | undefined;
  min?: string;
  max?: string;
  page?: string;
  sort?: string;
  id?: string;
  entry?: string;
  origin?: string;
  deepLink?: string;
}

export type ReviewRequestParams = {
  text?: string;
  rating?: number;
  overallScore?: number;
  listingId?: string;
};

export type InitialState = {
  experiments: ExperimentsBag;
  baseURL: string;
  pathname: string;
  language: string;
  userGuid: string;
  messages: Messages;
  isMobileUserAgent: boolean;
  cookieConsent?: string;
  tagManager?: string;
};

export type WixNextApiRequest = NextApiRequest & {
  aspects: AspectStore;
  get: (arg: string) => string;
};

export type WixNextApiResponse = NextApiResponse & {
  locals: {
    appContext: BootstrapContext;
    hadron: HadronClient;
    config: Record<string, string>;
    project?: Project;
    partnerAccount?: PartnerAccount;
  };
};

export interface HadronConfig {
  readonly clientTopology: {
    locale_dataset_data_static_url: string;
    tag_manager_static_url: string;
    cookie_consent_policy_client_static_url: string;
  };
  readonly secrets: {
    private_key: string;
    answers_keyId: string;
    answers_secret: string;
    traceability_media_secret: string;
  };
}

export type LocaleDataTranslations = Record<string, string>;

export interface SEOProps {
  title: string;
  metaTags: Array<any>;
  links: Array<any>;
  structureData?: string;
}

export enum ProfilePageType {
  MARKETPLACE = 'MARKETPLACE',
  AGENCY = 'AGENCY',
  MARKETPLACE_NO_LEADS = 'MARKETPLACE_NO_LEADS',
}
export interface ProviderRating {
  completedProjects?: number;
  averageReviewRating?: number;
  reviewsCount?: number;
}

export interface StateEnrichment {
  enabled?: boolean;
  statusType?: Type;
}

export interface PartnerProfileEnriched extends PartnerProfile {
  providerRating?: ProviderRating;
  projects: ClientProject[];
  projectsCursor?: string;
}

export type PartnerProfileSlugError = {
  error: typeof PROFILE_NOT_FOUND | typeof PROFILE_NOT_EU_VALID;
};

export type FindEnrichedPartnerProfileBySlugResponse =
  | PartnerProfileSlugError
  | (PartnerProfileEnriched & {
      state?: StateEnrichment;
      profilePageType?: ProfilePageType;
    });

export function isPartnerProfileSlugError(
  profileResponse: FindEnrichedPartnerProfileBySlugResponse,
): profileResponse is PartnerProfileSlugError {
  return (profileResponse as PartnerProfileSlugError)?.error !== undefined;
}

declare global {
  interface Window {
    __STATE__: InitialState;
  }
}

export type ClientProjectCard = {
  id: string;
  briefs?: Array<ClientCardBrief>;
  createdDate?: string | number | Date;
  isAutomatch?: boolean;
  serviceName?: string | null;
};

export interface ClientCardProfessionalInfo {
  businessName?: string;
  professionalId?: string;
  profileImage?: string;
  slug?: string;
}

export interface ClientCardBrief
  extends Pick<ClientProject, 'id' | 'revision'> {
  professionalInfo?: ClientCardProfessionalInfo;
  status: string;
  reviewScore?: number;
  url?: string;
}

export type CardProjectRowData = {
  profileImage?: string;
  businessName?: string;
  slug?: string;
  status: string;
  reviewScore?: number;
  id?: string;
  url?: string;
  professionalInfo?: ClientCardProfessionalInfo;
};

export type ServicePriceData = {
  min: number;
  max: number;
  lowRange: number;
  highRange: number;
};

export type FeatureItem = {
  type: string;
  id: string;
};

export interface ImageSource extends MediaSource {}

export interface AppName {
  name: string;
  isKey: boolean;
}

export type Template = {
  id: string;
  siteType: string;
  title: string;
  price: number;
  language: string;
  languages: string[];
  industries: string[];
  reviews: {
    reviewCount: number;
    averageRating: number;
  };
  purchaseCount: number;
  previewId: string;
  description: string;
  media: {
    additionalImages: Array<ImageSource>;
    animatedPreview: ImageSource;
    thumbnail: ImageSource;
  };
  features: Array<FeatureItem>;
  platform: string;
  url: string;
  isOwnerView: boolean;
  hasAdditionalPurchaseApps: boolean;
  accountId: string;
  partnerProfileDetails: {
    usePublicProfile: boolean;
    email: string;
    profileImage: string;
    businessName: string;
    businessWebsite?: string;
    slug?: string;
  };
  templateId: string;
  apps?: AppName[];
  privacyPolicyUrl?: string | null;
  hasVeloOrBlocks: boolean;
};

export interface TemplateGalleryCard
  extends Omit<TemplateListing, 'partnerProfileDetails'> {
  partnerProfileDetails?: { businessName?: string };
  privacyPolicyUrl?: string | null;
  review?: {
    average: number;
    total: number;
  };
  purchaseCount: number;
  title?: string | null;
  description?: string | null;
  language?: string | null;
  price?: number | null;
  industries?: Industry[];
  styles?: Style[];
  apps?: AppName[];
  siteTypes?: SiteType[];
  media?: Media;
  hasVeloOrBlocks?: boolean;
}

export const SITE_TYPES = {
  ALL: 'ALL',
  ONLINE_STORE: 'online_store',
  PORTFOLIO: 'portfolio_cv',
  BUSINESS_SITE: 'business_site',
  LANDING_PAGE: 'landing_page',
  BLOG: 'blog',
  SERVICES: 'services',
} as const;
export type SiteType = keyof typeof SITE_TYPES;

export const LANGUAGES = {
  ALL: 'ALL',
  ENGLISH: 'en',
  JAPANESE: 'ja',
  FRENCH: 'fr',
  ITALIAN: 'it',
  SPANISH: 'es',
  GERMAN: 'de',
  HINDI: 'hi',
} as const;

const { ALL, ...LANGUAGES_WITHOUT_ALL } = LANGUAGES;

export type LanguageFilterValue = keyof typeof LANGUAGES;

export const PRICES = {
  ALL: 'ALL',
  FREE: 'free',
  PAID: 'paid',
} as const;
export type PriceValue = keyof typeof PRICES;

export const INDUSTRIES = {
  BEAUTY_AND_WELLNESS: 'beauty_and_wellness',
  CREATIVE: 'creative',
  EDUCATION: 'education',
  FIELD_AND_CONSTRUCTION: 'field_and_construction',
  FITNESS: 'fitness',
  HEALTH: 'health',
  HOTELS: 'hotels',
  NON_PROFIT_AND_COMMUNITIES: 'non_profit_and_communities',
  PHOTOGRAPHERS: 'photographers',
  EVENTS: 'events',
  REAL_ESTATE: 'real_estate',
  RESTAURANTS: 'restaurants',
  SCIENCE_AND_TECHNOLOGY: 'science_and_technology',
  TRAVEL_AND_TOURISM: 'travel_and_tourism',
} as const;
export type Industry = keyof typeof INDUSTRIES;

export const FEATURES = {
  STORE: 'store',
  BOOKING: 'booking',
  BLOG: 'blog',
  MEMBERS_AREA: 'members_area',
  MULTILINGUAL: 'multilingual',
  PRICING_PLANS: 'pricing_plans',
  EVENTS: 'events',
  RESTAURANT: 'restaurant',
  USER_CODE: 'user_code',
  CMS: 'cms',
} as const;
export type Feature = keyof typeof FEATURES;

export const FEATURES_TYPE = {
  APP: 'app',
  ADVANCED_TOOLS: 'advanced_tools',
} as const;

export const STYLES = {
  DARK: 'dark',
  MINIMAL: 'minimal',
  ILLUSRATIVE: 'illustrative',
  TYPOGRAPHY: 'typography',
} as const;
export type Style = keyof typeof STYLES;

export const EDITOR_TYPES = {
  EDITOR: 'EDITOR',
  EDITORX: 'EDITORX',
  ADI: 'ADI',
  STUDIO: 'STUDIO',
} as const;

//export type Platform = keyof typeof EDITOR_TYPES;

export const PLATFORM_PATH_PARAMS = {
  STUDIO: 'studio',
  WEBSITE: 'website',
} as const;

export type IPLATFORM_PATH_PARAMS =
  (typeof PLATFORM_PATH_PARAMS)[keyof typeof PLATFORM_PATH_PARAMS];

export type Filters = {
  price: string;
  styles: string[];
  languages: string[];
  features: string[];
  industries: string[];
  siteType: string;
  search: string;
  partnerId?: string;
};

export type FiltersWithoutSearch = Omit<Filters, 'search' | 'partnerId'>;

export const VALID_SORTING_METHODS = [
  'relevance',
  'price_low_to_high',
  'price_high_to_low',
  'newest',
  'rating',
  'purchases',
] as const;
export type SortingMethod = (typeof VALID_SORTING_METHODS)[number];
export enum ValidSortingMethods {
  RELEVANCE = 'relevance',
  PRICE_LOW_TO_HIGH = 'price_low_to_high',
  PRICE_HIGH_TO_LOW = 'price_high_to_low',
  NEWEST = 'newest',
  RATING = 'rating',
  PURCHASES = 'purchases',
}

export type IHandleBIGenericClick = ({
  clickType,
  clickData,
}: {
  clickType: string;
  clickData?: any;
}) => void;

export enum VIEW_MODE {
  DESKTOP = 'desktop',
  TABLET = 'tablet',
  MOBILE = 'mobile',
}

export type ViewTemplateData = {
  id?: string;
  hasVeloOrBlocks: boolean;
  businessName: string;
  templateUrl: string;
  ownerAccountId: string;
  price?: number;
  title?: string;
  editorType: Platform;
  templateId?: string;
  templateName?: string;
  media: string;
  longThumbnail: string;
  privacyPolicyUrl?: string;
};

export interface TemplateQueryResponse {
  templates: TemplateGalleryCard[];
  numberOfPages: number;
  count: number;
  hasNextPage: boolean;
  nextCursor?: string;
}
export const REDEMPTION_ERROR_MESSAGES = {
  NOT_FOUND: 'NOT_FOUND',
  SHAREABLE_LINK_DISABLED: 'SHAREABLE_LINK_DISABLED',
  SHAREABLE_KEY_MISSING: 'SHAREABLE_KEY_MISSING',
  USER_NOT_OWNER_OF_CHECKOUT: 'USER_NOT_OWNER_OF_CHECKOUT',
  USED_LINK: 'USED_LINK',
} as const;

export type TRedemptionErrors =
  (typeof REDEMPTION_ERROR_MESSAGES)[keyof typeof REDEMPTION_ERROR_MESSAGES];

export type templateCheckout = {
  checkoutURL?: string;
  checkoutId?: string;
};

export type TCurrencyData = {
  currencyCode: string;
  conversionRate: number;
};

export interface DisputeProviderOption {
  id: string;
  businessName?: string;
  hasActiveDispute: boolean;
}

export type DisputeProviderOptions = DisputeProviderOption[];

export interface ProfileProps {
  onContactClicked: () => void;
  onCopyClicked: () => void;
  onScheduleMeetingClicked: () => void;
  headerOnClick: () => void;
  showBack: boolean;
  certifications: any[];
  scrollToSection: (
    section: (typeof PROFILE_PAGE_SECTIONS)[keyof typeof PROFILE_PAGE_SECTIONS],
    isMiniContactPanelVisible?: boolean,
  ) => void;
  sessionId: string;
  partnerProfile: any;
  isRenderedInModal: boolean;
  numberOfTemplates: number;
  navigationRefs: React.MutableRefObject<any>;
  copy: (text: string) => Promise<void>;
  profileUrl: string;
  contactStatus: 'success' | 'error' | null;
  clearStatusMap: Record<'success' | 'error', () => void>;
}

export type Service = AmbassadorService & { minPrice: number };
export type Category = Omit<AmbassadorCategory, 'services'> & {
  services?: Service[];
};

export type Leaves<T, L0 = T[keyof T]> = L0 extends Record<string, any>
  ? Leaves<L0>
  : L0;
